import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogActions,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Button,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';
import { ReactComponent as DropdownArrow } from '../assets/DropdownArrow.svg';
import DialogTransition from './Common/DialogTransition';
import DatePickerField from './Common/DatePicker';
import useOrganization from '../hooks/UseOrganization';
import REGEX from '../const/Regex';

/**
 * @component KeyTabDialog
 * @description KeyTabDialog - Component for managing ApiKeys, allowing users to add and edit ApiKeys.
 * @param {Object} props - The props object containing component properties.
 * @param {boolean} props.open - Indicates whether the dialog is open or not.
 * @param {Function} props.setOpen - Function to set the state of the dialog's open status.
 * @param {string} props.mode - The mode of the dialog, either 'create' or 'edit'.
 * @param {Object} props.editApiKey - The ApiKey being edited in 'edit' mode.
 * @param {Function} props.setMode - Function to set the mode of the dialog.
 * @param {Function} props.setApiKeyList - Function to set the list of ApiKeys.
 */

const KeyTabDialog = ({
  open,
  setOpen,
  mode,
  editApiKey,
  setMode,
  setApiKeyList,
  pageIndex,
  pageSize,
  searchTerm,
  setPageCount,
  setTotalCount,
}) => {
  const {
    createApiKey,
    updateApiKey,
    getApiKeyList,
    getApplicationList,
    getProductService,
  } = useOrganization();
  const orgName = useSelector((state) => state.common.orgName);
  const [applicationList, setApplicationList] = useState([]);
  const [productServiceData, setProductServiceData] = useState([]);

  const today = new Date().toISOString().split('T')[0]; // 'YYYY-MM-DD' format

  useEffect(() => {
    if (orgName) {
      getApplicationList(orgName).then((resp) => {
        setApplicationList(resp?.data);
      });
    }
  }, [orgName]);

  /**
   * Initial form values for the KeyTabDialog component.
   * @type {Object}
   * @property {string} name - The name input field value.
   * @property {string} headers - The headers input field value.
   * @property {Array} activeEvents - The active events selected.
   */

  const initialValues = {
    product: {
      label: '',
      id: '',
    },
    productService: {
      name: '',
      id: '',
    },
    name: '',
    description: '',
    expiryOption: 'no',
    expiryDate: today,
  };

  /**
   * Validation schema for the KeyTabDialog component form.
   * @type {Object}
   * @property {Object} name - Validation for the name field.
   * @property {Object} headers - Validation for the headers field.
   * @property {Object} activeEvents - Validation for the active events field.
   */

  const validationSchema = object({
    product: object().shape({
      label: string().required('Required'),
      id: string().required('Required'),
    }),
    productService: object().shape({
      name: string().required('Required'),
      id: string().required('Required'),
    }),
    name: string()
      .matches(REGEX.ORGANIZATION_NAME, 'Invalid name')
      .min(2, 'Key name must be at least 2 characters')
      .max(255, 'Key name must be at most 255 characters')
      .required('Required'),
    description: string()
      .min(2, 'Description must be at least 2 characters')
      .max(320, 'Description must be at most 320 characters')
      .required('Required'),
    expiryOption: string().required('Required'),
    expiryDate: string(),
  });

  /**
   * Custom Formik form handler using useFormik hook.
   *
   * @param {Object} initialValues - The initial values for the form fields.
   * @param {Object} validationSchema - The Yup validation schema for the form.
   * @param {string} mode - The mode of the form, either 'edit' or 'create'.
   * @param {function} updateApiKey - Function to update an existing ApiKey.
   * @param {function} createApiKey - Function to create a new ApiKey.
   * @param {function} getApiKeyList - Function to fetch the list of ApiKeys.
   * @param {function} setApiKeyList - Function to update the state with the fetched ApiKeys.
   * @param {function} setMode - Function to set the form mode.
   * @param {function} setOpen - Function to control the visibility of the form dialog.
   * @param {Object} orgName - The ID of the organization.
   * @param {Object} editApiKey - The ApiKey object being edited (if in edit mode).
   * @returns {Object} Formik form handler with form state and helpers.
   */

  const formikForm = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const expiryData =
        values?.expiryOption === 'no'
          ? 'never'
          : moment(values?.expiryDate).format('DD/MM/YYYY');
      const payload = {
        name: values?.name,
        description: values?.description,
        product: values?.product?.id,
        service: values?.productService?.id,
        date: expiryData,
      };
      // Handle form submission logic here
      if (mode === 'edit') {
        updateApiKey(payload, editApiKey?.id, orgName).then(() => {
          setMode('create');
          getApiKeyList(orgName, pageIndex, pageSize, searchTerm).then(
            (resp) => {
              setApiKeyList(resp?.data?.data);
              setPageCount(resp?.data?.max_pages);
              setTotalCount(resp?.data?.total_count);
            }
          );
        });
        setMode('create');
      } else {
        createApiKey(payload, orgName).then(() => {
          getApiKeyList(orgName, pageIndex, pageSize, searchTerm).then(
            (resp) => {
              setApiKeyList(resp?.data?.data);
              setPageCount(resp?.data?.max_pages);
              setTotalCount(resp?.data?.total_count);
            }
          );
        });
      }
      setOpen(false); // Close the dialog after successful submission (optional)
      formikForm.resetForm();
    },
  });

  /**
   * @function handleReaderInput
   * @description handles the input field for the product selection in the form.
   * @param {object} params - The parameters for the Autocomplete component.
   * @returns {JSX} - renders The TextField component for Autocomplete
   * @example handleReaderInput(params)
   */
  const handleReaderInput = useCallback(
    (params) => {
      return (
        <TextField
          label="Product"
          required
          {...params}
          name="product"
          placeholder="Select product"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.product?.id && formikForm?.touched?.product
              ? formikForm.errors.product?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.product?.id) &&
            formikForm?.touched?.product
          }
        />
      );
    },
    [formikForm]
  );

  /**
   * @function handleReaderInput
   * @description handles the input field for the product selection in the form.
   * @param {object} params - The parameters for the Autocomplete component.
   * @returns {JSX} - renders The TextField component for Autocomplete
   * @example handleReaderInput(params)
   */
  const handleReaderInputProductService = useCallback(
    (params) => {
      return (
        <TextField
          label="Product service"
          required
          {...params}
          name="productService"
          placeholder="Select product service"
          InputLabelProps={{
            shrink: true,
          }}
          onBlur={formikForm.handleBlur}
          helperText={
            formikForm?.errors?.productService?.id &&
            formikForm?.touched?.productService
              ? formikForm.errors.productService?.id
              : null
          }
          error={
            Boolean(formikForm?.errors?.productService?.id) &&
            formikForm?.touched?.productService
          }
        />
      );
    },
    [formikForm]
  );

  useEffect(() => {
    if (mode === 'edit') {
      formikForm.setValues({
        name: editApiKey?.name,
        description: editApiKey?.description,
        product: {
          label: editApiKey?.product_name,
          id: editApiKey?.product_id,
        },
        productService: {
          name: editApiKey?.service_name,
          id: editApiKey?.service_id,
        },
        expiryOption: editApiKey?.expires_at === 'never' ? 'no' : 'yes',
        expiryDate:
          editApiKey?.expires_at !== 'never'
            ? moment(editApiKey.expires_at).format('YYYY-MM-DD')
            : '',
      });
    }
  }, [mode]);

  const handleChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );

  const handleOnClose = useCallback(() => {
    setMode('create');
    formikForm?.handleReset();
    setOpen((prev) => !prev);
    setProductServiceData([]);
  }, [setOpen, formikForm, setMode]);

  const handleExpiryOnChange = useCallback(
    (_e, value) => {
      formikForm.setFieldValue('expiryOption', value);

      if (value === 'yes' && !formikForm.values.expiryDate) {
        formikForm.setFieldValue(
          'expiryDate',
          new Date().toISOString().split('T')[0] // set today's date
        );
      }
      if (value === 'no') {
        formikForm.setFieldValue('expiryDate', '');
      }
    },
    [formikForm]
  );

  const productServiceFromProduct = useCallback((id) =>
    getProductService(orgName, id).then((res) => {
      setProductServiceData(res?.data);
    })
  );

  const handleReset = useCallback(() => {
    formikForm?.handleReset();
    if (mode === 'edit') {
      formikForm.setFieldValue('product', {
        label: editApiKey?.product_name,
        id: editApiKey?.product_id,
      });
      formikForm.setFieldValue('productService', {
        name: editApiKey?.service_name,
        id: editApiKey?.service_id,
      });
    }
  }, [formikForm, mode, editApiKey]);

  return (
    <Dialog
      maxWidth="xs"
      onClose={handleOnClose}
      TransitionComponent={DialogTransition}
      open={open}
    >
      <DialogTitle>
        {mode === 'edit' ? 'Edit API Key' : 'Create API Key'}
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        sx={{ position: 'absolute', right: 16, top: 8 }}
        onClick={handleOnClose}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <Autocomplete
              disabled={mode === 'edit'}
              options={
                applicationList?.length > 0
                  ? applicationList?.map((item) => ({
                      id: item?.id,
                      label: item?.name,
                    }))
                  : []
              }
              popupIcon={<DropdownArrow />}
              noOptionsText="No product available."
              disableClearable={true}
              getOptionLabel={(item) => (item?.label ? item?.label : '')}
              isOptionEqualToValue={(option, val) =>
                option?.label === val?.label
              }
              value={formikForm.values.product}
              onChange={(_e, newValue) => {
                formikForm.setFieldValue('product', newValue);
                productServiceFromProduct(newValue?.id);
              }}
              renderInput={handleReaderInput}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              disabled={mode === 'edit'}
              options={
                productServiceData?.map((item) => ({
                  id: item.id,
                  name: item.name,
                })) || []
              }
              popupIcon={<DropdownArrow />}
              noOptionsText="No product service available."
              disableClearable
              getOptionLabel={(option) => option?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={formikForm.values.productService}
              onChange={(_e, newValue) => {
                formikForm.setFieldValue('productService', newValue);
              }}
              renderInput={handleReaderInputProductService}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              required
              label="Key name"
              placeholder="Enter API key name"
              name="name"
              value={formikForm?.values?.name}
              onChange={handleChange}
              aria-label="name"
              onBlur={formikForm?.handleBlur}
              InputProps={{
                endAdornment: (
                  <Tooltip
                    title={
                      <Typography>
                        - Name should start with a character. <br />
                        - Numbers (0-9) are allowed. <br />- Underscore(_) and
                        hyphen (-) are allowed.
                      </Typography>
                    }
                    arrow
                    placement="top"
                  >
                    <InfoOutlinedIcon />
                  </Tooltip>
                ),
              }}
              helperText={
                formikForm?.errors?.name && formikForm?.touched?.name
                  ? formikForm?.errors?.name
                  : null
              }
              error={
                Boolean(formikForm?.errors?.name) && formikForm?.touched?.name
              }
            />
          </Grid>
          <Grid container item alignItems="center" spacing={2}>
            <Grid item xs={4.3}>
              <Typography variant="body4">Expiry option*</Typography>
            </Grid>
            <Grid item xs={7.7}>
              <RadioGroup
                row
                name="expiryOption"
                value={formikForm.values.expiryOption}
                onChange={handleExpiryOnChange}
                helperText={
                  formikForm?.errors?.expiryOption &&
                  formikForm?.touched?.expiryOption
                    ? formikForm?.errors?.expiryOption
                    : null
                }
                error={
                  Boolean(formikForm?.errors?.expiryOption) &&
                  formikForm?.touched?.expiryOption
                }
              >
                <FormControlLabel
                  control={
                    <Radio
                      value="no"
                      checked={formikForm.values.expiryOption === 'no'}
                    />
                  }
                  label="Never expire"
                />
                <FormControlLabel
                  control={
                    <Radio
                      value="yes"
                      checked={formikForm.values.expiryOption === 'yes'}
                    />
                  }
                  label="Pick a date"
                />
              </RadioGroup>
            </Grid>
          </Grid>
          {formikForm.values.expiryOption === 'yes' && (
            <Grid item xs={12}>
              <DatePickerField
                label="Expiry date *"
                value={
                  formikForm.values.expiryDate
                    ? moment(formikForm.values.expiryDate)
                    : null
                }
                onChange={(date) =>
                  formikForm.setFieldValue(
                    'expiryDate',
                    date ? date.toDate() : null
                  )
                }
                error={
                  formikForm.touched.expiryDate &&
                  Boolean(formikForm.errors.expiryDate)
                }
                helperText={
                  formikForm.touched.expiryDate && formikForm.errors.expiryDate
                }
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              label="Description"
              required
              placeholder="Enter description"
              name="description"
              value={formikForm?.values?.description}
              onChange={handleChange}
              aria-label="description"
              onBlur={formikForm?.handleBlur}
              helperText={
                formikForm?.errors?.description &&
                formikForm?.touched?.description
                  ? formikForm?.errors?.description
                  : null
              }
              error={
                Boolean(formikForm?.errors?.description) &&
                formikForm?.touched?.description
              }
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleReset}>
          Reset
        </Button>
        <Button variant="contained" onClick={formikForm.handleSubmit}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

KeyTabDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  editApiKey: PropTypes.shape({
    product_name: PropTypes.string,
    product_id: PropTypes.number,
    service_id: PropTypes.number,
    service_name: PropTypes.string,
    expiry: PropTypes.string,
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    expires_at: PropTypes.string,
    headers: PropTypes.oneOfType([PropTypes.object]).isRequired,
    events: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        title: PropTypes.string,
      })
    ),
    organization: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
  setMode: PropTypes.func.isRequired,
  setApiKeyList: PropTypes.func.isRequired,
  pageIndex: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setPageCount: PropTypes.func.isRequired,
  setTotalCount: PropTypes.func.isRequired,
};

export default KeyTabDialog;
